import { createModel } from '@rematch/core';
import { generatePath } from 'react-router-dom';
import { toast } from 'react-toastify';
import { toastErrorDeduped } from '../../utils/toast-dedupe';

// Constants
import URL from '../../constants/api-constants';

// Types
import type { RootModel } from './index';

// Http
import http from '../http/http-common';
import axios, { AxiosError, AxiosRequestConfig, GenericAbortSignal } from 'axios';
import CycleStatus from '../../enums/cycle-status.enum';
import { CycleController } from './controllers';
import { CyclePackage, IoTDataResponse } from './cycles';
import { IControllerSettings } from '../../types/controller-types';
import { IScreensByContractResponse } from '../../types/screen';
export type Screen = {
  id: string;
  name: string;
  contractId: string;
  critical_deviation: number;
};

export type ScreenCycle = {
  id: string;
  name: string;
  status: CycleStatus;
  cycle_controllers: CycleController[];
  iotDataResponse: IoTDataResponse[];
  packages: CyclePackage[];
  report_id: number;
  controllersSettings: Record<string, IControllerSettings>;
  end_at: string;
};

export type ScreenWithCycles = {
  id: string;
  name: string;
  contractId: string;
  critical_deviation: number;
  cycles: ScreenCycle[];
};

const DEFAULT_STATE: { screens: Screen[]; screen: Screen | null; screensWithCycles: ScreenWithCycles[] } = {
  screensWithCycles: null,
  screens: [],
  screen: null,
};

export const screens = createModel<RootModel>()({
  state: DEFAULT_STATE,
  reducers: {
    set(state, payload) {
      return {
        ...state,
        ...payload,
      };
    },
    setOne(state, payload) {
      return {
        ...state,
        screen: payload,
      };
    },
    setList(state, payload) {
      return {
        ...state,
        screens: payload,
      };
    },
    resetOne(state) {
      return {
        ...state,
        screen: DEFAULT_STATE.screen,
      };
    },
    resetList(state) {
      return {
        ...state,
        screens: DEFAULT_STATE.screens,
      };
    },
    resetScreensWithCycles(state) {
      return {
        ...state,
        screensWithCycles: DEFAULT_STATE.screensWithCycles,
      };
    },
    resetState() {
      return DEFAULT_STATE;
    },
  },
  effects: (dispatch) => {
    return {
      async getOneById(params: { screenId: string; signal?: GenericAbortSignal }) {
        try {
          const path = generatePath(URL.SCREEN, { id: params.screenId });
          const config: AxiosRequestConfig<{ signal?: GenericAbortSignal }> = {};
          if (params?.signal) {
            config.signal = params.signal;
          }
          const response = await http.get(path, config);

          dispatch.screens.setOne(response.data);
          return response.data;
        } catch (error: unknown) {
          if (axios.isCancel(error)) {
            return;
          }

          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getAllWithCyclesByContractId(data: { contractId: string; query?: { status: CycleStatus } }) {
        const { contractId, query } = data;
        try {
          const path = generatePath(URL.CONTRACT_SCREENS_CYCLES, { contractId });

          const config: AxiosRequestConfig = {};

          if (query) {
            config.params = query;
          }

          const response = await http.get(path, config);

          dispatch.screens.set({ screensWithCycles: response.data });
          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getAllByContractId(data: { contractId: string; query?: { name?: string } }) {
        const { contractId, query } = data;
        try {
          const path = generatePath(URL.CONTRACT_SCREENS, { contractId });

          const config: AxiosRequestConfig = {};

          if (query) {
            config.params = query;
          }

          const response = await http.get<IScreensByContractResponse[]>(path, config);

          dispatch.screens.setList(response.data);
          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getAllByLocationId(data: { locationId: string; query?: { name?: string, hasCycles?: boolean } }) {
        const { locationId, query } = data;
        try {
          const path = generatePath(URL.LOCATION_SCREENS, { locationId });

          const config: AxiosRequestConfig = {};

          if (query) {
            config.params = query;
          }

          const response = await http.get<IScreensByContractResponse[]>(path, config);

          dispatch.screens.setList(response.data);
          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toastErrorDeduped(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toastErrorDeduped(error.message);
          } else {
            toastErrorDeduped('An unexpected error occurred');
          }
          throw error;
        }
      },

      async create(payload: { name: string; contractId: string }) {
        try {
          const response = await http.post(URL.SCREENS, payload);

          toast.success('Screen created');
          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async update(
        data: {
          screenId: string;
          payload: {
            name?: string;
            deletedCycleId?: string;
            newCycleId?: string;
            critical_deviation?: number;
          };
        },
        state,
      ) {
        try {
          const { screenId, payload } = data;
          const path = generatePath(URL.SCREEN, { id: screenId });
          const response = await http.put(path, payload);

          dispatch.screens.setOne(response.data);
          toast.success('Screen updated');

          const newList = state.screens.screens.map((screen) => (screen.id === screenId ? response.data : screen));

          dispatch.screens.setList(newList);

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async delete(id: string, state) {
        try {
          const response = await http.delete(generatePath(URL.SCREEN, { id }));

          const newList =
            state.screens.screens.length > 0 ? state.screens.screens.filter((screen) => screen.id !== id) : [];

          dispatch.screens.setList(newList);

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },
    };
  },
});
